import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import { ShopProvider } from './context/ShopContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { NavigationService } from './services/navigationService';
import { ErrorService } from './services/errorService';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import ServiceDetails from './pages/ServiceDetails';
import BlogPost from './pages/BlogPost';
import BlogList from './pages/BlogList';
import ProductDetails from './pages/ProductDetails';
import SellerProfile from './pages/SellerProfile';
import ShopProfile from './pages/ShopProfile';
import Contact from './pages/Contact';
import CountyInfo from './pages/CountyInfo';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Marketplace from './pages/Marketplace';
import CommunityHub from './pages/CommunityHub';
import MyFarms from './components/farm/MyFarms';
import FarmCalculator from './components/farm/FarmCalculator';
import Payment from './components/payment/Payment';
import LogisticsCoordination from './components/logistics/LogisticsCoordination';
import DashboardLayout from './components/dashboard/DashboardLayout';
// Remove: import InfoSearch from './pages/InfoSearch';
import AgriDashboard from './pages/AgriDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import FarmDetails from './pages/farm/FarmDetails';
import SmartFarming from './pages/farm/SmartFarming';
import WeatherPage from './pages/farm/WeatherPage';
import FarmTasksIntegration from './components/farm/details/FarmTasksIntegration';
import ImportExportAnalysis from './pages/ImportExportAnalysis';
import TestPage from './pages/TestPage';
import TestSupabase from './pages/TestSupabase';
import LogoutButton from './components/auth/LogoutButton';
import EmailVerification from './pages/EmailVerification';
import WorkerManagementPage from './pages/dashboard/WorkerManagementPage';
import MyShops from './pages/dashboard/MyShops';
import ManageProducts from './pages/dashboard/ManageProducts';
import SellerOrders from './pages/dashboard/SellerOrders';
import ShopSettings from './pages/dashboard/ShopSettings';
import CampaignManager from './pages/dashboard/CampaignManager';
import WealthCenter from './pages/dashboard/WealthCenter';
import DealRoom from './pages/dashboard/DealRoom';
import NotificationsPage from './pages/dashboard/NotificationsPage';
import SubscriptionManagement from './pages/dashboard/SubscriptionManagement';
import AdminPortal from './pages/dashboard/AdminPortal';
import CarbonCredits from './pages/CarbonCredits';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import OrderTracking from './pages/OrderTracking';
import UserDashboard from './pages/UserDashboard';
import PageTransition from './components/common/PageTransition';

/**
 * PageWrapper component that applies PageTransition to its children
 * Uses the current location key to trigger the transition on route changes
 */
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <PageTransition key={location.pathname}>
      {children}
    </PageTransition>
  );
};

/**
 * ProtectedRoute component that checks authentication status
 * Redirects to login page if user is not authenticated
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {JSX.Element} Either the protected children or a redirect to login
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

/**
 * DashboardRoute component that wraps protected routes with the dashboard layout
 * Ensures routes are both protected and displayed within the dashboard UI
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render within dashboard
 * @returns {JSX.Element} Dashboard layout with the protected children
 */
const DashboardRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <DashboardLayout>{children}</DashboardLayout>
    </ProtectedRoute>
  );
};

/**
 * AppRoutes component that defines all application routes
 * Organizes routes into public and protected sections
 * Provides analytics context to the entire application
 * 
 * @returns {JSX.Element} Application routing configuration with providers
 */
const AppRoutes: React.FC = () => {
  const { loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Update navigation service with current path
    NavigationService.updateCurrentPath(location.pathname);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  console.log("AppRoutes component rendering");
  return (
    <AnalyticsProvider>
      <ShopProvider>
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <main className="flex-grow">
            <ErrorBoundary>
              <PageWrapper>
                <Routes>
                  {/* Public Routes - Accessible without authentication */}
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:serviceId" element={<ServiceDetails />} />
                  <Route path="/blog" element={<BlogList />} />
                  <Route path="/blog/:postId" element={<BlogPost />} />
                  <Route path="/products/:productId" element={<ProductDetails />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/counties" element={<CountyInfo />} />
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/marketplace/product/:id" element={<ProductDetails />} />
                  <Route path="/marketplace/seller/:id" element={<SellerProfile />} />
                  <Route path="/shops/:shopSlug" element={<ShopProfile />} />
                  <Route path="/account" element={
                    <ProtectedRoute>
                      <UserDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  } />
                  <Route path="/order-success" element={
                    <ProtectedRoute>
                      <OrderSuccess />
                    </ProtectedRoute>
                  } />
                  <Route path="/order-tracking/:orderId" element={
                    <ProtectedRoute>
                      <OrderTracking />
                    </ProtectedRoute>
                  } />
                  <Route path="/community" element={<CommunityHub />} />
                  {/* Remove the commented InfoSearch route entirely */}
                  <Route path="/test" element={<TestPage />} />
                  <Route path="/test-supabase" element={<TestSupabase />} />
                  <Route path="/agri-dashboard" element={
                    <ErrorBoundary>
                      <AgriDashboard />
                    </ErrorBoundary>
                  } />
                  <Route path="/import-export" element={
                    <ErrorBoundary>
                      <ImportExportAnalysis />
                    </ErrorBoundary>
                  } />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/verify-email" element={<EmailVerification />} />

                  {/* Protected Dashboard Routes - Require authentication */}
                  <Route path="/dashboard" element={
                    <DashboardRoute>
                      <Dashboard />
                    </DashboardRoute>
                  } />
                  <Route path="/dashboard/my-shops" element={<DashboardRoute><MyShops /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/settings" element={<DashboardRoute><ShopSettings /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/campaigns" element={<DashboardRoute><CampaignManager /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/wealth" element={<DashboardRoute><WealthCenter /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/deals" element={<DashboardRoute><DealRoom /></DashboardRoute>} />
                  <Route path="/dashboard/notifications" element={<DashboardRoute><NotificationsPage /></DashboardRoute>} />
                  <Route path="/dashboard/subscriptions" element={<DashboardRoute><SubscriptionManagement /></DashboardRoute>} />
                  <Route path="/dashboard/admin" element={<DashboardRoute><AdminPortal /></DashboardRoute>} />
                  <Route path="/dashboard/products" element={<DashboardRoute><ManageProducts /></DashboardRoute>} />
                  <Route path="/dashboard/orders" element={<DashboardRoute><SellerOrders /></DashboardRoute>} />
                  <Route path="/dashboard/my-farms" element={<DashboardRoute><MyFarms /></DashboardRoute>} />
                  <Route path="/dashboard/my-farms/:farmId/*" element={
                    <ProtectedRoute>
                      <FarmDetails />
                    </ProtectedRoute>
                  } />
                  <Route path="/dashboard/calculator" element={<DashboardRoute><FarmCalculator /></DashboardRoute>} />
                  <Route path="/dashboard/payment" element={<DashboardRoute><Payment /></DashboardRoute>} />
                  <Route path="/dashboard/marketplace" element={<DashboardRoute><Marketplace /></DashboardRoute>} />
                  <Route path="/dashboard/community" element={<DashboardRoute><CommunityHub /></DashboardRoute>} />
                  <Route path="/dashboard/logistics" element={<DashboardRoute><LogisticsCoordination /></DashboardRoute>} />
                  <Route path="/dashboard/carbon-credits" element={<DashboardRoute><CarbonCredits /></DashboardRoute>} />
                  <Route path="/dashboard/profile" element={<DashboardRoute><div>Profile Page (Coming Soon)</div></DashboardRoute>} />
                  <Route path="/dashboard/farm" element={<DashboardRoute><div>My Farm Page (Coming Soon)</div></DashboardRoute>} />
                  <Route path="/dashboard/smart-farming" element={<DashboardRoute><SmartFarming /></DashboardRoute>} />
                  <Route path="/dashboard/weather" element={<DashboardRoute><WeatherPage /></DashboardRoute>} />
                  <Route path="/dashboard/farm-tasks" element={<DashboardRoute><FarmTasksIntegration farmId="default-farm" /></DashboardRoute>} />
                  <Route path="/dashboard/import-export" element={<DashboardRoute><ImportExportAnalysis /></DashboardRoute>} />
                  <Route path="/dashboard/workers" element={<DashboardRoute><WorkerManagementPage /></DashboardRoute>} />

                  {/* 404 Route - Catches all undefined routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </PageWrapper>
            </ErrorBoundary>
          </main>
          <Footer />
        </div>
      </ShopProvider>
    </AnalyticsProvider>
  );
};

export default AppRoutes;
